import {
  border,
  BorderProps,
  color,
  ColorProps,
  compose,
  css,
  display,
  DisplayProps,
  EmotionComponent,
  FocusBorderColorProp,
  focusBorderColorProp,
  layout,
  LayoutProps,
  OverrideNativeElementProps,
  space,
  SpaceProps,
  styled,
  system,
  themeGet,
  typography,
  TypographyProps,
} from "@catchoftheday/theme";

const customStyles = compose(space, color, typography, border, display, layout);

type TextareaRawProps = {
  /** Whether the textarea is disabled */
  disabled?: boolean;
  /** border color when the textarea get hovered */
  hoverBorderColor?: string | string[];
  /** Whether the textarea is in error state */
  error?: boolean;
} & FocusBorderColorProp &
  SpaceProps &
  ColorProps &
  TypographyProps &
  BorderProps &
  DisplayProps &
  LayoutProps;

export type TextareaProps = OverrideNativeElementProps<
  TextareaRawProps,
  "textarea"
>;

const hoverColorTransform = system({
  hoverBorderColor: {
    property: "borderColor",
    scale: "colors",
  },
});

const activeTextColorTransform = system({
  activeTextColor: {
    property: "color",
    scale: "colors",
  },
});

export const Textarea: EmotionComponent<TextareaProps> = styled(
  "textarea"
)<TextareaProps>`
  outline: 0;
  transition: border-color ${themeGet("transitions.default")};
  ${customStyles}
  &:focus {
    ${focusBorderColorProp}
  }

  &:focus,
  &:hover:not(:disabled) {
    ${activeTextColorTransform}
  }

  &:disabled {
    cursor: not-allowed;
    color: ${themeGet("colors.typography.text")};
    background-color: ${themeGet("colors.ui.backgroundColor")};
  }

  ::placeholder {
    font-weight: ${themeGet("fontWeights.normal")};
    color: ${themeGet("colors.typography.inactive")};
  }
  ${(props) =>
    props.error &&
    css`
      border-color: ${themeGet("colors.ui.error")(props)};
      :not(:placeholder-shown) + label {
        color: ${themeGet("colors.ui.error")(props)};
      }
    `};

  ${(props) =>
    !props.error &&
    !props.disabled &&
    css`
      :focus {
        ${focusBorderColorProp(props)}
      }

      :hover {
        ${hoverColorTransform(props)}
      }

      :placeholder-shown:hover + label {
        color: ${themeGet("colors.secondaryDark")(props)};
      }

      :focus + label {
        color: ${themeGet("colors.secondary")(props)};
      }
    `};
`;

Textarea.displayName = "Textarea";

Textarea.defaultProps = {
  disabled: false,
  display: "inline",
  rows: 5,
  cols: 30,
  p: 5,
  bg: "white",
  width: "100%",
  color: "typography.text",
  fontSize: "base",
  lineHeight: "normal",
  border: 1,
  borderRadius: "default",
  borderColor: "ui.borderColor",
  textAlign: "left",
  hoverBorderColor: "secondaryDark",
  focusBorderColor: "secondary",
};

export default Textarea;
